/**
 * Gates protected customer routes behind a verified server session.
 *
 * Shows recovery actions when session bootstrap stalls.
 */
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  bootstrapSession,
  clearAuth,
  selectAuthChecked,
  selectAuthLoading,
  selectCurrentUser,
  selectIsAuthenticated,
} from '@/store/slices/authSlice';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

const SESSION_CHECK_TIMEOUT_MS = 10_000;
const APP_RESET_PATH = '/reset-app.html';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  requireGlobal?: boolean;
}

export default function ProtectedRoute({ children, allowedRoles, requireGlobal = false }: ProtectedRouteProps) {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const authChecked = useAppSelector(selectAuthChecked);
  const authLoading = useAppSelector(selectAuthLoading);
  const location = useLocation();
  const [sessionCheckTimedOut, setSessionCheckTimedOut] = useState(false);

  useEffect(() => {
    if (!authChecked && !authLoading) {
      dispatch(bootstrapSession());
    }
  }, [authChecked, authLoading, dispatch]);

  useEffect(() => {
    if (authChecked && !authLoading) {
      setSessionCheckTimedOut(false);
      return undefined;
    }

    const id = setTimeout(() => setSessionCheckTimedOut(true), SESSION_CHECK_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [authChecked, authLoading]);

  const handleSignInAgain = () => {
    dispatch(clearAuth());
    window.location.assign('/login');
  };

  if (!authChecked || authLoading) {
    return (
      <Box minHeight="60vh" display="flex" alignItems="center" justifyContent="center" p={4}>
        <Stack spacing={2} alignItems="center" maxWidth={560}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Checking your session...
          </Typography>
          {sessionCheckTimedOut && (
            <Alert severity="warning">
              <Typography fontWeight={600} mb={0.5}>
                We couldn't confirm your session.
              </Typography>
              <Typography variant="body2" mb={1.5}>
                The sign-in check is taking longer than expected. Refresh the
                page, or sign in again to restart onboarding.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button size="small" variant="contained" onClick={() => window.location.reload()}>
                  Refresh page
                </Button>
                <Button size="small" variant="outlined" onClick={() => window.location.assign(APP_RESET_PATH)}>
                  Reset app cache
                </Button>
                <Button size="small" variant="outlined" onClick={handleSignInAgain}>
                  Sign in again
                </Button>
              </Stack>
            </Alert>
          )}
        </Stack>
      </Box>
    );
  }

  if (!isAuthenticated || !user) {
    // Preserve the origin so login can return the user here (e.g. an NFC /assign
    // deep-link opened cold on a phone). Login reads location.state.from; other
    // callers ignore it, so this is behaviour-preserving for normal logins.
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search + location.hash }}
        replace
      />
    );
  }

  if (allowedRoles && user) {
    const userRole = (user.role || '').toUpperCase();
    // SUPER_ADMIN bypasses every page-level role gate (matches the backend
    // pattern — see controllers/anchorsController.ts, middleware/tenantContext.ts,
    // and the Sidebar's NAV_ITEMS filter). Anything ADMIN can see, SUPER_ADMIN can too.
    if (userRole !== 'SUPER_ADMIN') {
      const allowed = allowedRoles.map(r => r.toUpperCase());
      if (!allowed.includes(userRole)) {
        return (
          <Box p={4} textAlign="center">
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography color="text.secondary">
              You don't have permission to view this page. Contact your administrator.
            </Typography>
          </Box>
        );
      }
    }
  }

  if (requireGlobal && user.is_global !== true) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h5" gutterBottom>Access Denied</Typography>
        <Typography color="text.secondary">
          This page is restricted to Sentry Solutions platform operators.
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
}
